import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import StageContent from "./StageContent";
import MissionModal from "../../components/intro/MissionModal";
import MessageBeacon from "../../components/intro/MessageBeacon";
import { getRoomInfo } from "../../games/rooms";
import ScreenTooSmall from "../../components/ui/ScreenTooSmall";
import NotificationBadge from "../../components/ui/NotificationBadge";
import { useToast } from "../../components/ui/Toast";
import { useHandRaise } from "../../hooks/useHandRaise";
import { useAdvanceStage } from "../../hooks/useAdvanceStage";
import { useGameTimer } from "../../hooks/useGameTimer";
import CutsceneSlideshowFail from "../../components/ui/CutsceneSlideshowFail";
import TimerChip from "../../components/ui/TimerChip";
import UrgencyVignette from "../../components/ui/UrgencyVignette";
import HintNudgeModal from "../../components/ui/HintNudgeModal";
import AdvanceStageVoteModal from "../../components/ui/AdvanceStageVoteModal";
import NeonLoaderOverlay from "../../components/ui/NeonLoaderOverlay";
import { getOrigamiBriefText, ORIGAMI_STAGE2_BRIEF } from "../../games/origami/config/gameBriefs";
import GameHudLeft from "./GameHudLeft";
import GameHudRight from "./GameHudRight";
import GameDrawerSession from "./GameDrawerSession";
import GameDrawerTeam from "./GameDrawerTeam";
import { getRoleLabel } from "./gameRoles";
import GameRoomPreviewModal from "./GameRoomPreviewModal";

function Game() {
  const { code } = useParams();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  const [me, setMe] = useState(null);
  const [players, setPlayers] = useState([]);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [introOpen, setIntroOpen] = useState(false);
  const [introSeen, setIntroSeen] = useState(false);
  const [showSessionPanel, setShowSessionPanel] = useState(false);
  const [showGamePanel, setShowGamePanel] = useState(false);
  const [hintSignal, setHintSignal] = useState(0);
  const [inventory, setInventory] = useState({});
  const [roomPreviewOpen, setRoomPreviewOpen] = useState(false);
  const [roomPreview, setRoomPreview] = useState(null);
  const toast = useToast();
  const [hintNudgeOpen, setHintNudgeOpen] = useState(false);

  const showFailCutsceneRef = useRef(false);
  const isCutsceneActiveRef = useRef(false); // bloque le navigate pendant cutscene succès
  const intervalRef = useRef(null);

  const { timeLeft, sessionDuration, syncTimer } = useGameTimer({
    API,
    code,
    isCutsceneActiveRef,
    onTimeUp: () => setShowFailCutscene(true),
  });


  const [showFailCutscene, setShowFailCutscene] = useState(false);

  useEffect(() => { showFailCutsceneRef.current = showFailCutscene; }, [showFailCutscene]);

  const [morse, setMorse] = useState({ morseActive: false, morseStartedAt: null, morseResetCounter: 0, serverNow: null });
  const handleMorseUpdated = (data) => { if (!data) return; setMorse(prev => ({ ...prev, ...data })); };

  const playerId = Number(sessionStorage.getItem(`playerId:${code}`));
  const introKey = useMemo(() => `introSeen:${code}:${playerId}`, [code, playerId]);
  const stageIntroKey = useCallback(
    (stage) => `introSeen:${code}:${playerId}:stage:${stage}`,
    [code, playerId]
  );

  const { myHandRaised, toggleHandRaise, detectNewHandRaises } = useHandRaise({
    API, code, me, players, setMe, setPlayers
  });
  const raisedPlayers = players.filter(p => p.handRaised && p.id !== me?.id);
  const raisedCount = players.filter(p => p.handRaised).length;

  const [leftTab, setLeftTab] = useState("mission");
  const [missionFilter, setMissionFilter] = useState("recent"); // "recent"|"resolved"|"old"

  const hintKey = `roomPreviewHintSeen:${code}:${playerId}`;

  const [missionGuide, setMissionGuide] = useState([]);
  const [notif, setNotif] = useState({ mission: 0, shared: 0 });
  const prevMissionKeysRef = useRef(new Set());
  const leftTabRef = useRef(leftTab);
  useEffect(() => { leftTabRef.current = leftTab; }, [leftTab]);

  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isDesktopHud = introSeen && viewport.w >= 1420;
  const centerW = isDesktopHud ? viewport.w - 288 * 2 : viewport.w;
  const centerMaxH = Math.floor(centerW);
  const mobileSessionBadge = (notif.mission || 0) + (notif.shared || 0);

  const openIntro = () => setIntroOpen(true);
  const closeIntro = () => {
    setIntroOpen(false);
    if (!introSeen) { sessionStorage.setItem(introKey, "1"); setIntroSeen(true); }
  };

  const {
    advanceGate, setAdvanceGate,
    advanceModalOpen, setAdvanceModalOpen,
    requestNextStageVote, setMyNextStageVote,
  } = useAdvanceStage({ API, code, me, players, state, navigate });

  useEffect(() => {
    if (!playerId) { navigate(`/play/${code}`); return; }

    const fetchAll = async () => {
      try {
        // ── VAGUE 1 : 3 requêtes indépendantes en parallèle ──────────────────
        const [sessRes, resPlayers, resState] = await Promise.all([
          axios.get(`${API}/api/sessions/${code}`),
          axios.get(`${API}/api/sessions/${code}/players`),
          axios.get(`${API}/api/game/${code}/state/${playerId}`).catch(err => {
            if (err?.response?.status === 404) return { data: null };
            throw err;
          }),
        ]);

        const session = sessRes.data;
        const nextPlayers = resPlayers.data.players;
        const stateData = resState.data;

        if (stateData === null) {
          clearInterval(intervalRef.current); // ← stoppe le polling immédiatement
          sessionStorage.removeItem(`playerId:${code}`);
          setLoading(false);
          navigate(`/play/${code}`);
          return;
        }

        const stage = stateData?.stage || 1;

        // Early return si session terminée
        if (session.status === "used") {
          if (showFailCutsceneRef.current) return;
          if (isCutsceneActiveRef.current) return;
          setTimeout(() => {
            setLoading(false);
            navigate(`/scoreboard/${code}`);
          }, 800);
          return;
        }

        // Setter isCutsceneActiveRef seulement si session encore in_progress
        if (session?.infraState?.stage3?.door?.unlocked === true) {
          isCutsceneActiveRef.current = true;
        }

        // Détection mains levées
        await detectNewHandRaises(nextPlayers, playerId);

        // stageIntro check (sessionStorage, synchrone)
        let shouldOpenStageIntro = false;
        try {
          const seen = sessionStorage.getItem(stageIntroKey(stage)) === "1";
          if (stage === 2 && !seen) {
            shouldOpenStageIntro = true;
            sessionStorage.setItem(stageIntroKey(stage), "1");
          }
        } catch {
          if (stage === 2) shouldOpenStageIntro = true;
        }

        // ── VAGUE 2 : requêtes qui dépendent de stage ─────────────────────────
        const [resGuide, morseRes] = await Promise.all([
          axios.get(`${API}/api/sessions/${code}/mission-guide`, { params: { stage } }),
          axios.get(`${API}/api/sessions/${code}/morse`).catch(() => ({ data: null })),
        ]);

        const nextItems = resGuide.data.items || [];

        // Mission guide diff
        const nextByKey = new Map(nextItems.map(x => [x.key, x]));
        const newlyAddedItems = [];
        for (const [k, item] of nextByKey.entries()) {
          if (!prevMissionKeysRef.current.has(k)) newlyAddedItems.push(item);
        }
        prevMissionKeysRef.current = new Set(nextByKey.keys());
        for (const it of newlyAddedItems) toast.info(it.text, { title: "Mission" });

        syncTimer(session);

        // ── SETSTATE EN BLOC ──────────────────────────────────────────────────
        setAdvanceGate(session?.infraState?.advanceGate || null);
        setPlayers(nextPlayers);
        setMe(nextPlayers.find(p => p.id === playerId));
        setState(stateData);
        setMissionGuide(nextItems);
        setIntroSeen(sessionStorage.getItem(introKey) === "1");
        if (shouldOpenStageIntro) setIntroOpen(true);
        if (newlyAddedItems.length > 0 && leftTabRef.current !== "mission") {
          setNotif(n => ({ ...n, mission: n.mission + newlyAddedItems.length }));
        }

        if (morseRes.data) setMorse(morseRes.data);

      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };

    fetchAll();
    intervalRef.current = setInterval(fetchAll, 2000);
    return () => clearInterval(intervalRef.current);
  }, [code, playerId, navigate, introKey]);

  const MIN_WIDTH = 1024;
  const [isTooSmall, setIsTooSmall] = useState(false);

  useEffect(() => {
    const check = () => setIsTooSmall(window.innerWidth < MIN_WIDTH);
    check(); window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const [roomPreviewInfoOpen, setRoomPreviewInfoOpen] = useState(false);
  const player1 = players.find(p => p.pageVariant === 1) || null;
  const isPreviewingPlayer1 = roomPreview?.playerId && player1?.id && roomPreview.playerId === player1.id;

  useEffect(() => {
    if (!roomPreviewOpen) return;
    const onKeyDown = (e) => { if (e.key === "Escape") { setRoomPreviewOpen(false); setRoomPreview(null); } };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [roomPreviewOpen]);

  const danger = timeLeft !== null && timeLeft <= 60 * 5;
  const warn = timeLeft !== null && timeLeft > 60 * 5 && timeLeft <= 10 * 60;
  const urgencyLevel = danger ? "danger" : warn ? "warn" : "none";

  const hintNudgeKey = `hintNudgeSeen:${code}:${playerId}`;
  useEffect(() => {
    if (timeLeft === null || (!warn && !danger)) return;
    try {
      const seen = sessionStorage.getItem(hintNudgeKey) === "1";
      if (seen) return;
      sessionStorage.setItem(hintNudgeKey, "1");
      setHintNudgeOpen(true);
    } catch { setHintNudgeOpen(true); }
  }, [warn, danger, timeLeft, hintNudgeKey]);

  if (isTooSmall) return <ScreenTooSmall minWidth={MIN_WIDTH} title="Jeu en cours" subtitle={`Le jeu n'est pas jouable sur mobile (largeur ≥ ${MIN_WIDTH}px).`} />;
  if (loading || !me || !state) return <NeonLoaderOverlay label="Connexion au canal sécurisé…" />;

  const formatTime = (sec) => {
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const openRoomPreview = (p) => {
    const room = getRoomInfo(state.gameId ?? 1, state.stage, p.pageVariant);
    if (!room?.icon) return;
    setRoomPreview({ playerId: p.id, label: room.label, icon: room.icon, activity: room.activity, pseudo: p.pseudo, role: getRoleLabel(p), stage: state.stage || 1 });
    sessionStorage.setItem(hintKey, "1");
    setRoomPreviewOpen(true);
  };
  const closeRoomPreview = () => { setRoomPreviewOpen(false); setRoomPreview(null); };

  const voteGateActive = !!advanceGate?.active && Number(advanceGate?.stage) === Number(state?.stage);
  const myVote = voteGateActive ? (advanceGate?.votes?.[String(me.id)] || "pending") : null;
  const waitingCount = voteGateActive ? players.filter(p => (advanceGate?.votes?.[String(p.id)] || "pending") === "pending").length : 0;
  const showMorseInPreview = isPreviewingPlayer1 && state.stage === 1 && !!morse?.morseActive && !!morse?.morseStartedAt;

  return (
    <div className="min-h-[calc(100vh)] bg-black text-white flex overflow-x-hidden">
      <UrgencyVignette level={urgencyLevel} />
      <HintNudgeModal open={hintNudgeOpen} level={urgencyLevel} stage={state?.stage || null} onClose={() => setHintNudgeOpen(false)} onOpenHints={() => setHintSignal(n => n + 1)} />

      {introSeen && <GameHudLeft timeLeft={timeLeft} formatTime={formatTime} sessionDuration={sessionDuration} code={code} me={me} toggleHandRaise={toggleHandRaise} myHandRaised={myHandRaised} voteGateActive={voteGateActive} setAdvanceModalOpen={setAdvanceModalOpen} myVote={myVote} waitingCount={waitingCount} leftTab={leftTab} setLeftTab={setLeftTab} setNotif={setNotif} notif={notif} leftTabRef={leftTabRef} state={state} missionGuide={missionGuide} missionFilter={missionFilter} setMissionFilter={setMissionFilter} inventory={inventory} />}

      {/* ZONE DE JEU */}
      <main className="relative flex-1 min-w-0 flex items-center justify-center select-none">
        {introSeen && state.component ? (
          <StageContent
            syncStartedAt={morse?.morseStartedAt} syncResetCounter={morse?.morseResetCounter || 0} serverNow={morse?.serverNow}
            onMorseUpdated={handleMorseUpdated}
            gameId={state.gameId} componentName={state.component}
            canAdvance={requestNextStageVote} isCanAdvance={state?.canAdvance === true}
            code={code} players={players} centerMaxH={centerMaxH}
            playerId={me.id} pseudo={me.pseudo} pageVariant={state.pageVariant}
            hintSignal={hintSignal} raisedCount={raisedCount} raisedPlayers={raisedPlayers}
            onInventoryUpdate={(partial) => setInventory(prev => ({ ...prev, [me.id]: { ...(prev[me.id] || {}), ...partial } }))}
            onSuccessCutsceneStart={() => { isCutsceneActiveRef.current = true; }}
            onSuccessCutsceneEnd={() => { isCutsceneActiveRef.current = false; }}
            onNavigateToScoreboard={() => { isCutsceneActiveRef.current = false; navigate(`/scoreboard/${code}`); }}
          />
        ) : (
          <div className="text-slate-400 text-sm">Chargement de la scène…</div>
        )}
      </main>

      {/* TIMER MOBILE */}
      {introSeen && !isDesktopHud && timeLeft !== null && (
        <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[70] pointer-events-none [@media(min-width:1420px)]:hidden">
          <TimerChip timeLeft={timeLeft} formatTime={formatTime} compact className="bg-black/55 border-white/15" />
        </div>
      )}

      {/* BOUTONS MOBILE */}
      {introSeen && (
        <div className="fixed bottom-4 left-0 right-0 z-40 flex items-center justify-between px-3 [@media(min-width:1420px)]:hidden">
          {/* Bouton gauche — cyan */}
          <button
            onClick={() => setShowSessionPanel(true)}
            style={{
              position: "relative",
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "10px 16px",
              fontSize: 10, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase",
              color: "rgba(6,182,212,.95)",
              background: "repeating-linear-gradient(45deg,rgba(6,182,212,.03) 0px,rgba(6,182,212,.03) 1px,transparent 1px,transparent 7px),rgba(2,8,20,.92)",
              border: "1px solid rgba(6,182,212,.35)",
              clipPath: "polygon(8px 0%,100% 0%,calc(100% - 8px) 100%,0% 100%)",
              backdropFilter: "blur(12px)",
              boxShadow: "0 0 14px rgba(6,182,212,.12), inset 0 0 0 1px rgba(6,182,212,.08)",
              cursor: "pointer", fontFamily: "ui-monospace,monospace",
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(6,182,212,1)", boxShadow: "0 0 8px rgba(6,182,212,1)", flexShrink: 0 }} />
            <span>Session & Outils</span>
            <NotificationBadge count={mobileSessionBadge} />
          </button>

          {/* Bouton droit — indigo */}
          <button
            onClick={() => setShowGamePanel(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "10px 16px",
              fontSize: 10, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase",
              color: "rgba(165,180,252,.95)",
              background: "repeating-linear-gradient(45deg,rgba(99,102,241,.04) 0px,rgba(99,102,241,.04) 1px,transparent 1px,transparent 7px),rgba(2,4,20,.92)",
              border: "1px solid rgba(99,102,241,.4)",
              clipPath: "polygon(8px 0%,100% 0%,calc(100% - 8px) 100%,0% 100%)",
              backdropFilter: "blur(12px)",
              boxShadow: "0 0 14px rgba(99,102,241,.15), inset 0 0 0 1px rgba(99,102,241,.08)",
              cursor: "pointer", fontFamily: "ui-monospace,monospace",
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(99,102,241,1)", boxShadow: "0 0 8px rgba(99,102,241,.9)", flexShrink: 0 }} />
            <span>Équipe & Indices</span>
          </button>
        </div>
      )}

      {introSeen && <GameHudRight openIntro={openIntro} setHintSignal={setHintSignal} state={state} players={players} me={me} morse={morse} openRoomPreview={openRoomPreview} setRoomPreviewInfoOpen={setRoomPreviewInfoOpen} />}

      {/* OVERLAY INTRO */}
      {!introSeen && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/95 backdrop-blur-sm">
          <MessageBeacon onClick={openIntro} docked={false} />
          <div className="absolute bottom-10 text-gray-300 text-sm">Cliquez sur l'icône pour lire le brief</div>
        </div>
      )}

      <MissionModal open={introOpen} onClose={closeIntro}
        title={state.stage === 2 ? "Notes de mission" : "Brief de mission"}
        variant={state.stage === 2 ? "log" : "default"}
        body={state.stage === 2 ? ORIGAMI_STAGE2_BRIEF : getOrigamiBriefText(me.pseudo)}
      />

      {/* DRAWER SESSION (mobile) */}
      {introSeen && showSessionPanel && (
        <GameDrawerSession
          onClose={() => setShowSessionPanel(false)}
          timeLeft={timeLeft} formatTime={formatTime} sessionDuration={sessionDuration}
          code={code} me={me}
          toggleHandRaise={toggleHandRaise} myHandRaised={myHandRaised}
          voteGateActive={voteGateActive} setAdvanceModalOpen={setAdvanceModalOpen}
          myVote={myVote} waitingCount={waitingCount}
          leftTab={leftTab} setLeftTab={setLeftTab} setNotif={setNotif} notif={notif} leftTabRef={leftTabRef}
          state={state} missionGuide={missionGuide} missionFilter={missionFilter} setMissionFilter={setMissionFilter}
          inventory={inventory}
        />
      )}

      {/* DRAWER ÉQUIPE (mobile) */}
      {introSeen && showGamePanel && (
        <GameDrawerTeam
          onClose={() => setShowGamePanel(false)}
          openIntro={openIntro} setHintSignal={setHintSignal}
          state={state} players={players} me={me} morse={morse}
          openRoomPreview={openRoomPreview}
        />
      )}

      {/* ROOM PREVIEW MODAL */}
      {roomPreviewOpen && roomPreview?.icon && (
        <GameRoomPreviewModal
          roomPreview={roomPreview}
          showMorseInPreview={showMorseInPreview}
          morse={morse}
          onClose={closeRoomPreview}
        />
      )}

      {introSeen && advanceModalOpen && advanceGate?.active && (
        <AdvanceStageVoteModal code={code} me={me} players={players} state={state} gate={advanceGate} onClose={() => setAdvanceModalOpen(false)} onVote={setMyNextStageVote} />
      )}

      {/* MODALE INFO APERÇU DE SALLE */}
      {roomPreviewInfoOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 px-4" onClick={() => setRoomPreviewInfoOpen(false)}>
          <div className="relative w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}
            style={{ background: "#04050f", border: "1px solid rgba(99,102,241,.35)", borderRadius: 12, boxShadow: "0 0 40px rgba(99,102,241,.15)" }}>
            {/* Coins déco */}
            <div style={{ position: "absolute", top: 0, left: 0, width: 16, height: 16, borderTop: "1px solid rgba(99,102,241,.5)", borderLeft: "1px solid rgba(99,102,241,.5)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", bottom: 0, right: 0, width: 16, height: 16, borderBottom: "1px solid rgba(99,102,241,.5)", borderRight: "1px solid rgba(99,102,241,.5)", pointerEvents: "none" }} />

            {/* Header */}
            <div style={{ padding: "12px 16px 10px", borderBottom: "1px solid rgba(255,255,255,.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontSize: 9, letterSpacing: ".2em", textTransform: "uppercase", color: "rgba(99,102,241,.7)", fontWeight: 700, fontFamily: "ui-monospace,monospace" }}>
                ◈ Aperçu de salle
              </div>
              <button onClick={() => setRoomPreviewInfoOpen(false)}
                style={{ width: 24, height: 24, borderRadius: 5, border: "1px solid rgba(255,255,255,.08)", background: "rgba(255,255,255,.04)", color: "rgba(148,163,184,.8)", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                ✕
              </button>
            </div>

            {/* Corps */}
            <div style={{ padding: "14px 16px 16px" }}>
              {state.stage === 3 ? (
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "rgba(165,180,252,.95)", fontFamily: "ui-sans-serif,system-ui", marginBottom: 8 }}>
                    Vous êtes tous dans la même salle
                  </div>
                  <p style={{ fontSize: 12, color: "rgba(148,163,184,.75)", fontFamily: "ui-sans-serif,system-ui", lineHeight: 1.6 }}>
                    À l'étape 3, toute l'équipe se retrouve dans le même espace. Il n'y a plus de salles distinctes à visualiser — concentrez-vous sur la mission finale ensemble.
                  </p>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "rgba(165,180,252,.95)", fontFamily: "ui-sans-serif,system-ui", marginBottom: 8 }}>
                    Comment accéder à l'aperçu ?
                  </div>
                  <p style={{ fontSize: 12, color: "rgba(148,163,184,.75)", fontFamily: "ui-sans-serif,system-ui", lineHeight: 1.6, marginBottom: 10 }}>
                    Chaque joueur est dans une salle différente. Pour voir la salle de quelqu'un :
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                    {[
                      { icon: "🔍", text: "Clique sur la miniature 🔍 à droite d'une carte joueur" },
                      { icon: "👁", text: "Ou clique directement sur la carte d'un joueur" },
                    ].map((tip, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "7px 10px", borderRadius: 7, background: "rgba(99,102,241,.07)", border: "1px solid rgba(99,102,241,.15)" }}>
                        <span style={{ fontSize: 14, flexShrink: 0 }}>{tip.icon}</span>
                        <span style={{ fontSize: 11, color: "rgba(226,232,240,.85)", fontFamily: "ui-sans-serif,system-ui", lineHeight: 1.5 }}>{tip.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showFailCutscene && (
        <CutsceneSlideshowFail isCanAdvance={true} canAdvance={() => navigate(`/scoreboard/${code}`)} onSkip={() => navigate(`/scoreboard/${code}`)} />
      )}
    </div>
  );
}

export default Game;